import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowLeft,
    Smartphone,
    Apple,
    Monitor,
    Download,
    Share,
    Plus,
    Check,
    AlertCircle,
    Globe,
} from "lucide-react";

// Local APK served from /public. The owner must place a file named
// HACCP-Manager.apk inside /app/frontend/public/ for this to work.
const APK_URL = "/HACCP-Manager.apk";

function detectDevice() {
    if (typeof navigator === "undefined") return "unknown";
    const ua = navigator.userAgent || "";
    if (/Android/i.test(ua)) return "android";
    if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
    return "desktop";
}

function detectBrowser() {
    if (typeof navigator === "undefined") return "unknown";
    const ua = navigator.userAgent || "";
    if (/SamsungBrowser/i.test(ua)) return "samsung";
    if (/CriOS|Chrome/i.test(ua)) return "chrome";
    if (/EdgiOS|Edg/i.test(ua)) return "edge";
    if (/FxiOS|Firefox/i.test(ua)) return "firefox";
    if (/Safari/i.test(ua)) return "safari";
    return "other";
}

export default function Scarica() {
    const [device, setDevice] = useState("unknown");
    const [browser, setBrowser] = useState("unknown");
    const [installPromptEvent, setInstallPromptEvent] = useState(null);
    const [installed, setInstalled] = useState(false);
    const [apkAvailable, setApkAvailable] = useState(true);

    useEffect(() => {
        setDevice(detectDevice());
        setBrowser(detectBrowser());

        // PWA install prompt (Chrome/Edge desktop, Chrome Android)
        const onBeforeInstallPrompt = (e) => {
            e.preventDefault();
            setInstallPromptEvent(e);
        };
        const onInstalled = () => setInstalled(true);
        window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
        window.addEventListener("appinstalled", onInstalled);

        // Verify APK file presence (HEAD)
        fetch(APK_URL, { method: "HEAD" })
            .then((r) => setApkAvailable(r.ok))
            .catch(() => setApkAvailable(false));

        return () => {
            window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
            window.removeEventListener("appinstalled", onInstalled);
        };
    }, []);

    const triggerPWAInstall = async () => {
        if (!installPromptEvent) return;
        installPromptEvent.prompt();
        try {
            await installPromptEvent.userChoice;
        } catch {
            // user dismissed
        }
        setInstallPromptEvent(null);
    };

    return (
        <div className="min-h-screen bg-bone text-stone-700" data-testid="scarica-page">
            <header className="border-b border-soft bg-white">
                <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Link to="/" className="inline-flex items-center gap-2 text-forest hover:opacity-80">
                        <ArrowLeft size={18} /> <span className="font-semibold">Torna al sito</span>
                    </Link>
                    <div className="inline-flex items-center gap-2 text-forest-dark">
                        <Download size={18} />
                        <span className="font-display font-semibold">Scarica HACCP Manager</span>
                    </div>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-6 py-10">
                <div className="text-center mb-10">
                    <h1 className="font-display text-3xl sm:text-4xl text-forest-dark mb-3">
                        Installa HACCP Manager sul tuo dispositivo
                    </h1>
                    <p className="text-stone-600 max-w-xl mx-auto">
                        Scegli la modalità di installazione in base al tuo dispositivo. Tutte le opzioni sono gratuite e veloci.
                    </p>
                </div>

                {/* DEVICE-SPECIFIC PRIMARY OPTION */}
                {device === "android" && (
                    <DownloadCard
                        icon={Smartphone}
                        title="Android"
                        recommended
                        primary={
                            <a
                                href={apkAvailable ? APK_URL : undefined}
                                download="HACCP-Manager.apk"
                                aria-disabled={!apkAvailable}
                                className={`btn-primary inline-flex items-center justify-center gap-2 w-full text-base px-6 py-3.5 ${
                                    !apkAvailable ? "opacity-50 pointer-events-none" : ""
                                }`}
                                data-testid="apk-download-btn"
                            >
                                <Download size={18} /> Scarica l&apos;app (APK)
                            </a>
                        }
                        steps={[
                            "Tocca il bottone qui sopra per scaricare il file APK",
                            'Quando Android chiede "App da fonte sconosciuta", tocca "Impostazioni" → abilita "Consenti da questa fonte"',
                            "Torna indietro → tocca \"Installa\"",
                            'Se Play Protect dice "App non riconosciuta", tocca "Installa comunque" (è normale: l\'app non è ancora sul Play Store)',
                            "Aprila dal launcher come una qualsiasi app",
                        ]}
                        note={!apkAvailable && "L'APK non è ancora disponibile sul server. Riprova fra qualche minuto o usa l'opzione PWA qui sotto."}
                    />
                )}

                {device === "ios" && (
                    <DownloadCard
                        icon={Apple}
                        title="iPhone / iPad"
                        recommended
                        primary={
                            browser !== "safari" ? (
                                <div className="rounded-xl bg-amber-50 text-amber-800 p-4 text-sm flex items-start gap-2">
                                    <AlertCircle size={18} className="shrink-0 mt-0.5" />
                                    <span>
                                        Per installare su iPhone/iPad devi usare <b>Safari</b>. Apri questa pagina con Safari e poi segui i passaggi qui sotto.
                                    </span>
                                </div>
                            ) : (
                                <div className="rounded-xl bg-forest-50 text-forest-dark p-4 text-sm font-medium">
                                    Tocca l&apos;icona <Share size={14} className="inline mx-1" /> Condividi in basso, poi scorri e scegli &quot;Aggiungi alla schermata Home&quot;.
                                </div>
                            )
                        }
                        steps={[
                            "In basso (Safari) tocca l'icona Condividi (quadratino con freccia in su)",
                            'Scorri il menu e tocca "Aggiungi alla schermata Home"',
                            'Conferma in alto a destra con "Aggiungi"',
                            "Apri HACCP Manager dalla schermata Home come una vera app",
                        ]}
                    />
                )}

                {device === "desktop" && (
                    <DownloadCard
                        icon={Monitor}
                        title="Computer (Windows / Mac / Linux)"
                        recommended
                        primary={
                            installPromptEvent ? (
                                <button
                                    onClick={triggerPWAInstall}
                                    className="btn-primary inline-flex items-center justify-center gap-2 w-full text-base px-6 py-3.5"
                                    data-testid="pwa-install-btn"
                                >
                                    <Plus size={18} /> Installa HACCP Manager
                                </button>
                            ) : installed ? (
                                <div className="rounded-xl bg-forest text-white p-4 text-sm flex items-center gap-2 justify-center font-semibold">
                                    <Check size={18} /> Installata! Cercala nel menu Start o Launchpad.
                                </div>
                            ) : (
                                <div className="rounded-xl bg-stone-100 text-stone-700 p-4 text-sm">
                                    Per installare clicca l&apos;icona <b>+</b> nella barra degli indirizzi del browser, oppure dal menu del browser scegli &quot;Installa HACCP Manager&quot;.
                                </div>
                            )
                        }
                        steps={[
                            "Apri questa pagina con Chrome, Edge o Brave",
                            'Clicca il bottone "Installa" qui sopra (o l\'icona + nella barra indirizzi)',
                            'Conferma con "Installa"',
                            "Trovi l'app nel menu Start (Windows) o nel Launchpad (Mac) come un programma normale",
                        ]}
                    />
                )}

                {/* ALWAYS-AVAILABLE FALLBACK: PWA install */}
                {device !== "android" && device !== "desktop" && (
                    <div className="mt-6 text-center text-sm text-stone-500">
                        Dispositivo non riconosciuto? Sei sempre il benvenuto su{" "}
                        <a href="https://haccpmanger.it" className="text-forest font-semibold">haccpmanger.it</a>
                    </div>
                )}

                {/* SECONDARY OPTIONS */}
                <div className="grid md:grid-cols-2 gap-4 mt-8">
                    {device !== "android" && (
                        <SmallOption
                            icon={Smartphone}
                            title="Sei su Android?"
                            desc="Scarica l'APK firmato direttamente per il tuo telefono."
                            cta={
                                <a
                                    href={apkAvailable ? APK_URL : undefined}
                                    download="HACCP-Manager.apk"
                                    aria-disabled={!apkAvailable}
                                    className={`btn-ghost inline-flex items-center gap-1.5 text-sm px-4 py-2 ${
                                        !apkAvailable ? "opacity-50 pointer-events-none" : ""
                                    }`}
                                >
                                    <Download size={14} /> Scarica APK
                                </a>
                            }
                        />
                    )}
                    {device !== "ios" && (
                        <SmallOption
                            icon={Apple}
                            title="Hai un iPhone?"
                            desc="Apri questa pagina con Safari sull'iPhone e segui le istruzioni."
                            cta={
                                <span className="text-xs text-stone-500">
                                    Visita https://haccpmanger.it/scarica
                                </span>
                            }
                        />
                    )}
                </div>

                {/* ALTERNATIVE: USE IT WITHOUT INSTALLING */}
                <div className="mt-10 p-6 rounded-2xl border border-soft bg-white">
                    <div className="flex items-start gap-3">
                        <Globe size={22} className="text-forest shrink-0 mt-1" />
                        <div>
                            <h3 className="font-display text-lg text-forest-dark mb-1">Non vuoi installare niente?</h3>
                            <p className="text-sm text-stone-600 mb-3">
                                HACCP Manager funziona perfettamente anche dal browser, senza installare nulla. Apri{" "}
                                <a href="https://haccpmanger.it" className="text-forest font-semibold">haccpmanger.it</a>{" "}
                                e accedi al tuo account. I tuoi dati sono identici su tutti i dispositivi.
                            </p>
                            <Link
                                to="/register"
                                className="btn-ghost inline-flex items-center gap-2 text-sm px-4 py-2"
                                data-testid="scarica-register"
                            >
                                Inizia subito su web
                            </Link>
                        </div>
                    </div>
                </div>

                <p className="text-xs text-stone-400 text-center mt-10">
                    L&apos;app è in attesa di pubblicazione sul Google Play Store.{" "}
                    <Link to="/privacy" className="underline">Privacy</Link>{" "}·{" "}
                    <Link to="/termini" className="underline">Termini</Link>
                </p>
            </main>
        </div>
    );
}

function DownloadCard({ icon: Icon, title, recommended, primary, steps, note }) {
    return (
        <div className="bg-white rounded-3xl border border-soft p-6 sm:p-8 shadow-sm">
            <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 rounded-2xl bg-forest-50 text-forest grid place-items-center">
                    <Icon size={24} />
                </div>
                <div>
                    <h2 className="font-display text-xl text-forest-dark">{title}</h2>
                    {recommended && (
                        <p className="text-[11px] uppercase tracking-widest font-bold text-accent-warm">Consigliato per te</p>
                    )}
                </div>
            </div>

            <div className="mb-5">{primary}</div>
            {note && (
                <div className="rounded-xl bg-amber-50 text-amber-800 p-3 text-sm mb-4 flex items-start gap-2">
                    <AlertCircle size={16} className="shrink-0 mt-0.5" />
                    <span>{note}</span>
                </div>
            )}

            <ol className="space-y-2.5">
                {steps.map((s, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-stone-700">
                        <span className="shrink-0 w-6 h-6 rounded-full bg-forest-50 text-forest grid place-items-center text-xs font-bold">
                            {i + 1}
                        </span>
                        <span>{s}</span>
                    </li>
                ))}
            </ol>
        </div>
    );
}

function SmallOption({ icon: Icon, title, desc, cta }) {
    return (
        <div className="rounded-2xl border border-soft bg-bone-2 p-5">
            <div className="flex items-start gap-3 mb-3">
                <Icon size={20} className="text-stone-500 shrink-0 mt-0.5" />
                <div>
                    <h4 className="font-semibold text-stone-800">{title}</h4>
                    <p className="text-xs text-stone-600 mt-0.5">{desc}</p>
                </div>
            </div>
            {cta}
        </div>
    );
}
